/**
 * Trend-series construction for the Reports Overview page.
 *
 * Extracted from the page so it can be tested without rendering React. The behaviour it
 * guards (BugHerd #142): response time and satisfaction were Math.random() values marked
 * "Mock for now" even though the payload carries real responseTimeTrend and
 * satisfactionTrend arrays, and the volume series read `created`/`resolved`, which the
 * live shape ({ date, value, label }) does not have - so it plotted flat zeros beneath a
 * "Live" badge.
 */

export interface TrendPoint {
  date: string;
  tickets: number;
  /** Hours, matching summary.averageResponseTimeHours and the Avg Response Time card. */
  responseTime: number;
  satisfaction: number;
}

interface RawSeriesPoint {
  date?: string | null;
  value?: number | null;
  /** Demo-mode fixtures use `created` where the live payload uses `value`. */
  created?: number | null;
}

export interface TrendSources {
  ticketTrends?: unknown;
  responseTimeTrend?: unknown;
  satisfactionTrend?: unknown;
}

/**
 * Index a sibling series by date. The series are independent arrays and nothing
 * guarantees they align by position, so they are joined on date rather than index.
 */
const byDate = (series: unknown): Map<string, number> => {
  const map = new Map<string, number>();
  if (Array.isArray(series)) {
    series.forEach((point: RawSeriesPoint) => {
      if (point?.date != null) map.set(String(point.date), Number(point.value ?? 0));
    });
  }
  return map;
};

/**
 * Build the chart series. Every value comes from the response; a field with no source
 * resolves to 0 rather than an invented figure, and callers state its absence in the UI.
 */
export const buildTrendSeries = (
  sources: TrendSources,
  formatDate: (date: string) => string,
): TrendPoint[] => {
  const points = sources?.ticketTrends;
  if (!Array.isArray(points)) return [];

  const responseByDate = byDate(sources.responseTimeTrend);
  const satisfactionByDate = byDate(sources.satisfactionTrend);

  return points.map((point: RawSeriesPoint) => {
    const key = point?.date != null ? String(point.date) : '';
    return {
      date: point?.date ? formatDate(String(point.date)) : 'N/A',
      tickets: point?.created ?? point?.value ?? 0,
      responseTime: responseByDate.get(key) ?? 0,
      satisfaction: satisfactionByDate.get(key) ?? 0,
    };
  });
};
